import React, { useEffect, useRef, useState } from 'react';
import { Box, Flex, IconButton, Spinner, Text } from '@chakra-ui/react';
import { Document, Page, pdfjs } from 'react-pdf';

import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

type PdfFileSource =
  | string
  | ArrayBuffer
  | {
      url: string;
      httpHeaders?: Record<string, string>;
    };

interface PdfViewerProps {
  file: PdfFileSource;
}

export default function PdfViewer({ file }: PdfViewerProps) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [scale, setScale] = useState(1);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.15, 2.0));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.15, 0.6));

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="xl" overflow="hidden" bg="gray.50">
      <Flex
        justify="flex-end"
        align="center"
        px={3}
        py={2}
        borderBottomWidth="1px"
        borderColor="gray.200"
        bg="gray.100"
        gap={2}
      >
        <Text fontSize="sm" color="gray.600" mr={1}>
          Zoom: {(scale * 100).toFixed(0)}%
        </Text>
        <IconButton
          size="xs"
          aria-label="Zoom out"
          onClick={handleZoomOut}
          variant="outline"
        >
          -
        </IconButton>
        <IconButton
          size="xs"
          aria-label="Zoom in"
          onClick={handleZoomIn}
          colorScheme="blue"
        >
          +
        </IconButton>
      </Flex>

      <Box
        ref={containerRef}
        bg="white"
        px={{ base: 0, md: 2 }}
        py={{ base: 2, md: 3 }}
        overflow="hidden"
      >
        <Document
          file={file}
          onLoadSuccess={({ numPages: loadedPages }: { numPages: number }) =>
            setNumPages(loadedPages)
          }
          loading={
            <Box py={10} textAlign="center">
              <Spinner size="lg" color="blue.500" />
              <Text mt={3} color="gray.300">
                Loading PDF...
              </Text>
            </Box>
          }
          error={
            <Box py={10} textAlign="center">
              <Text color="red.400">Unable to load PDF preview.</Text>
            </Box>
          }
        >
          {numPages && containerWidth
            ? Array.from({ length: numPages }, (_, index) => (
                <Page
                  key={`page_${index + 1}`}
                  pageNumber={index + 1}
                  width={containerWidth * scale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              ))
            : null}
        </Document>
      </Box>
    </Box>
  );
}
